import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import './OrderBill.scss';
import { Download } from 'sushiweb/Icons';
import { Loader, Tooltip, CopyTextIcon } from '../../../UiKit';
import { userModel } from '../../../api';
import { EmptyState } from '../../commons';
import { InvalidSearchImg, upArrowIconGreen } from '../../../images/images';
import { getFullDate } from '../../commons/CommonMethods';
import { invoiceDownloadEvent } from '../../../analytics/events';
import { MetricsForJumboEvent } from '../../../analytics/constants';
import InfoToggle from '../../ComponentsV1/InfoToggle';
import { columnHeaderStyles } from './constants';
import { Weight } from 'lucide-react';
import { OrderTag } from '../../widgets/Order/OrderBasicInfo/OrderTags';
import ReceiversTable from './ReceiversTable';

export const OrderBill = ({ orderId = null }) => {
  const [orderBillData, setOrderBillData] = useState(null);
  const [error, setError] = useState(null);
  const [gstDetails, setGstDetails] = useState('');
  const [bistro_order_cutlery_details, setBistroOrderCutleryDetails] =
    useState('');

  const getOrderBillData = useCallback(() => {
    const querryParams = {};
    querryParams['order_id'] = orderId;
    userModel
      .getOrderBillDetails(querryParams)
      .then((response) => {
        if (!response || response?.isError) return setError(response);
        setGstDetails(response.gst_details);
        setBistroOrderCutleryDetails(response.cutlery_preference);
        setOrderBillData({
          ...response,
          // todo: temp hack , check zomato response
          order_items: response.order_items.map((item) => ({ item })),
        });
      })
      .catch((err) => {
        setError(err);
      });
  }, [orderId]);

  useEffect(() => {
    getOrderBillData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!orderBillData && !error) {
    return <Loader />;
  }
  if (error) {
    return (
      <EmptyState
        img={InvalidSearchImg}
        heading={error?.message || 'No data available!'}
        isPop={true}
      />
    );
  }
  return (
    <OrderBillComp
      orderBillData={orderBillData}
      orderId={orderId}
      gstDetails={gstDetails}
      bistro_order_cutlery_details={bistro_order_cutlery_details}
    />
  );
};

const OrderBillComp = ({
  orderBillData,
  orderId,
  gstDetails,
  bistro_order_cutlery_details,
}) => {
  const {
    currency,
    download_invoice_links,
    suborder_bills,
    show_download_invoice,
  } = orderBillData;

  const isOldContract = Array.isArray(download_invoice_links);
  const showDownloadButton = isOldContract
    ? download_invoice_links.length > 0
    : show_download_invoice;

  return (
    <div className='tw-relative tw-h-full tw-flex tw-flex-col tw-px-6'>
      <div className='popContent modalContent'>
        <div className='billTagList'>
          {orderBillData.net_weight && (
            <div className='weightTag tw-w-max'>
              <div className='tw-flex tw-items-center tw-gap-4 tw-text-[1.4rem] tw-font-thin'>
                <span className='tw-flex tw-items-center tw-leading-none tw-gap-2'>
                  {`Order Weight: ${orderBillData.net_weight}`}{' '}
                  <Weight size={18} />
                </span>
                {(gstDetails || bistro_order_cutlery_details) && <span>|</span>}
                {gstDetails && (
                  <OrderTag
                    className='tw-bg-grey-200 tw-text-zinc-800 !tw-shadow-none'
                    data-dd-privacy='mask'
                  >
                    {gstDetails}
                  </OrderTag>
                )}
                {bistro_order_cutlery_details && (
                  <OrderTag className='tw-bg-grey-200 tw-text-zinc-800 !tw-shadow-none'>
                    {bistro_order_cutlery_details}
                  </OrderTag>
                )}
              </div>
            </div>
          )}

          {orderBillData.promo_codes?.length > 0 ? (
            <div className='tw-w-full'>
              {orderBillData.promo_codes?.length > 1 ? (
                <div className='tw-flex tw-items-center tw-justify-center'>
                  <span className='tw-bg-indigo-100 tw-py-2 tw-px-2 tw-rounded-lg'>
                    Applied Promo Codes
                  </span>
                </div>
              ) : null}
              <div className='row tw-flex-wrap'>
                {orderBillData.promo_codes.map((promo_data) => (
                  <div className='promoTag' key={promo_data.promo_code}>
                    <div className='col'>
                      <p className='dark promo iconMarginR iconMarginL'>
                        <span>{`${
                          orderBillData.promo_codes?.length === 1
                            ? 'PROMO: '
                            : ''
                        }${promo_data.promo_code}`}</span>
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : null}
        </div>

        <ul className='dishes'>
          {orderBillData.order_items &&
            orderBillData.order_items.map((item, index) => {
              if (
                item.item.type === 'dish' ||
                item.item.type === 'BASE_ITEM' ||
                item.item.type === 'ADD_ON_ITEM' ||
                item.item.type === 'dote_product' ||
                item.item.type === 'SERVICE' ||
                item.item.type === 'PRODUCT'
              ) {
                return (
                  <li key={index} className='col'>
                    <ul className='totals'>
                      <li className='col'>
                        <div className='row'>
                          {item.item?.item_image_url && (
                            <Image
                              src={item.item.item_image_url}
                              data-dd-privacy='hidden'
                            />
                          )}
                          <div className='col item-info'>
                            <span className='dish-name pr'>
                              {item.item.itemName}{' '}
                              {item.item.product_url ? (
                                <Tooltip
                                  position='bottom'
                                  border={false}
                                  title={
                                    <>
                                      <a
                                        target={'_blank'}
                                        href={item.item.product_url}
                                        className='iconMarginL icon'
                                        rel='noreferrer'
                                      >
                                        <img
                                          src={upArrowIconGreen}
                                          alt='open link in product details'
                                        />
                                      </a>
                                    </>
                                  }
                                  label={item.item.product_url_tooltip}
                                />
                              ) : null}
                            </span>

                            {item.item.chooseText && (
                              <ul>
                                {item.item.chooseText
                                  .split(';')
                                  .map((variant, index) => (
                                    <li key={index}>
                                      <span className='description'>
                                        {variant}
                                      </span>
                                    </li>
                                  ))}
                              </ul>
                            )}
                            <div className='count pr'>
                              {`${item.item.quantity} `}
                              <span className='quantity'>
                                x {currency}
                                {item.item.unitCost}
                              </span>
                            </div>
                            {item.item.how_to_use_video ? (
                              <div className='flex items-center'>
                                <span className='tw-text-black'>
                                  Setup video link
                                </span>
                                <CopyTextIcon
                                  textToCopy={item.item.how_to_use_video}
                                />
                              </div>
                            ) : null}
                          </div>
                          <RightContainer>
                            <div
                              style={{
                                textAlign: 'right',
                                marginRight: '10px',
                              }}
                            >
                              <span>
                                {currency}
                                {item.item.totalCost}
                              </span>
                              {item.item.procurementCost &&
                              item.item.totalCost !==
                                item.item.procurementCost *
                                  item.item.quantity ? (
                                <span className='iconMarginL strikeThrough'>
                                  {currency}
                                  {item.item.procurementCost}
                                </span>
                              ) : null}
                            </div>
                            <TagsContainer>
                              {item.item.tags.map((tag) => {
                                return (
                                  <ProcuredQuantityWrapper
                                    key={tag.text}
                                    background={tag.bg_color}
                                    color={tag.color}
                                  >
                                    {tag.text}
                                  </ProcuredQuantityWrapper>
                                );
                              })}
                            </TagsContainer>
                          </RightContainer>
                        </div>
                        {item.item.offers?.length > 0 ? (
                          <div className='row'>
                            {item.item.offers.map((offer) => {
                              return (
                                <OfferDetailWrapper
                                  key={offer.text}
                                  background={offer.bg_color}
                                  color={offer.color}
                                >
                                  {offer.text}
                                </OfferDetailWrapper>
                              );
                            })}
                          </div>
                        ) : null}
                        {item?.item?.digital_ofse_receiver_details &&
                        Array.isArray(
                          item.item.digital_ofse_receiver_details,
                        ) &&
                        item.item.digital_ofse_receiver_details.length > 0 ? (
                          <ReceiverDetailsDisplay
                            receivers={item.item.digital_ofse_receiver_details}
                            itemId={item.item?.id}
                          />
                        ) : null}
                      </li>
                    </ul>
                    {typeof item.item.reducedCostAfterBogo !== 'undefined' && (
                      <div className='bottom-text'>
                        {typeof item.item.added_bottom_text !== 'undefined' ? (
                          <div className='col pr' style={{ color: '#E9B501' }}>
                            {item.item.added_bottom_text}
                          </div>
                        ) : (
                          <div className='col pr' style={{ color: 'green' }}>
                            Buy one get one offer applied
                          </div>
                        )}
                        <div>
                          <div className='price item-price right'>
                            <span>
                              {currency}
                              {item.item.reducedCostAfterBogo}
                            </span>
                          </div>
                        </div>
                      </div>
                    )}
                  </li>
                );
              }
              return null;
            })}
        </ul>
        <ul className='totals'>
          {orderBillData.order_items &&
            orderBillData.order_items.map((item, index) => {
              if (
                item.item.type !== 'dish' &&
                item.item.type !== 'BASE_ITEM' &&
                item.item.type !== 'ADD_ON_ITEM' &&
                item.item.type !== 'total' &&
                item.item.type !== 'total_merchant' &&
                item.item.type !== 'cash_to_be_collected' &&
                item.item.type !== 'dote_product' &&
                item.item.type !== 'additional_charge' &&
                item.item.type !== 'cashback_details' &&
                item.item.type !== 'SERVICE' &&
                item.item.type !== 'PRODUCT' &&
                item.item.type !== null
              ) {
                return (
                  <li className={`${item.item.type} row`} key={index}>
                    <div
                      className='name pr col'
                      style={{ color: item.item.itemNameColor }}
                    >
                      {item.item.itemName}
                    </div>
                    <div
                      className='price'
                      style={{ color: item.item.displayCostColor }}
                    >
                      {currency}
                      {item.item.totalCost}
                    </div>
                  </li>
                );
              } else {
                return null;
              }
            })}
        </ul>
        <ul className='totals'>
          {orderBillData.order_items &&
            orderBillData.order_items.map((item, index) => {
              if (item.item.type === 'total') {
                return (
                  <li className={`${item.item.type} row`} key={index}>
                    <div
                      className='name pr col'
                      style={{ color: item.item.itemNameColor }}
                    >
                      {item.item.itemName}
                    </div>
                    <div
                      className='price'
                      style={{ color: item.item.displayCostColor }}
                    >
                      {currency}
                      {item.item.totalCost}
                    </div>
                  </li>
                );
              } else {
                return null;
              }
            })}
        </ul>
        <ul className='totals'>
          {orderBillData.order_items &&
            orderBillData.order_items.map((item, index) => {
              if (item.item.type === 'cash_to_be_collected') {
                return (
                  <li className={`${item.item.type} row`} key={index}>
                    <div
                      className='name pr col'
                      style={{ color: item.item.itemNameColor }}
                    >
                      {item.item.itemName}
                    </div>
                    <div
                      className='price'
                      style={{ color: item.item.displayCostColor }}
                    >
                      {currency}
                      {item.item.displayCost}
                    </div>
                  </li>
                );
              } else {
                return null;
              }
            })}
        </ul>

        <ul className=''>
          {orderBillData.order_items &&
            orderBillData.order_items.map((item, index) => {
              if (item.item.type === 'cashback_details') {
                return (
                  <li className={`${item.item.type} row`} key={index}>
                    <div
                      className='name pr col'
                      style={{ color: item.item.itemNameColor }}
                    >
                      {item.item.itemName}
                    </div>
                    <div
                      className='price'
                      style={{ color: item.item.displayCostColor }}
                    >
                      {item.item.value || `${currency} ${item.item.totalCost}`}
                    </div>
                  </li>
                );
              } else {
                return null;
              }
            })}
        </ul>

        <ul className=''>
          {orderBillData.order_items &&
            orderBillData.order_items.map((item, index) => {
              if (item.item.type === 'additional_charge') {
                return (
                  <li className={`${item.item.type} row`} key={index}>
                    <div
                      className='name pr col'
                      style={{ color: item.item.itemNameColor }}
                    >
                      {item.item.itemName}
                    </div>
                    <div
                      className='price'
                      style={{ color: item.item.displayCostColor }}
                    >
                      {currency}
                      {item.item.totalCost}
                    </div>
                  </li>
                );
              } else {
                return null;
              }
            })}
        </ul>

        <SubOrderTable suborderBills={suborder_bills} />
      </div>

      {showDownloadButton && (
        <div className='tw-bottom-0 tw-left-0 tw-right-0 tw-bg-white tw-border-t'>
          <DownloadInvoice
            orderId={orderId}
            directLinks={isOldContract ? download_invoice_links : null}
          />
        </div>
      )}
    </div>
  );
};

function DownloadInvoice({ orderId, directLinks }) {
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState(null);

  const handleFetchInvoice = () => {
    if (directLinks) {
      invoiceDownloadEvent({ [MetricsForJumboEvent.order_id]: orderId });
      directLinks.forEach(({ download_links }) => {
        download_links?.forEach(({ url }) => {
          window.open(url, '_blank', 'noopener,noreferrer');
        });
      });
      return;
    }

    setLoading(true);
    setFetchError(null);
    userModel
      .downloadOrderInvoice({ order_id: orderId })
      .then((response) => {
        if (!response || response?.isError) {
          setFetchError('Failed to fetch invoice. Please try again.');
          return;
        }
        const downloadLinks = response?.download_invoice_links ?? [];
        if (downloadLinks.length > 0) {
          invoiceDownloadEvent({ [MetricsForJumboEvent.order_id]: orderId });
          downloadLinks.forEach(({ download_links }) => {
            download_links?.forEach(({ url }) => {
              window.open(url, '_blank', 'noopener,noreferrer');
            });
          });
        } else {
          setFetchError('Invoice not available yet.');
        }
      })
      .catch(() => setFetchError('Failed to fetch invoice. Please try again.'))
      .finally(() => setLoading(false));
  };

  return (
    <div className='greyStrip downloadLink tw-cursor-pointer'>
      <div className='iconMarginL tw-flex tw-items-center tw-gap-2'>
        <button
          onClick={handleFetchInvoice}
          disabled={loading}
          className='tw-flex tw-items-center tw-gap-1 tw-bg-transparent tw-border-none tw-cursor-pointer'
        >
          {loading ? 'Fetching...' : 'Download invoice/s'}
          {!loading && (
            <span className='iconMarginL iconMarginR'>
              <Download size={14} />
            </span>
          )}
        </button>
        {fetchError && (
          <span className='tw-text-red-500 tw-text-[1.2rem]'>{fetchError}</span>
        )}
      </div>
    </div>
  );
}

const calculateTax = (product) => {
  let result = 0;
  const keys = [
    'surcharge',
    'vat',
    'cst',
    'sgst_value',
    'cess_value',
    'cgst_value',
    'igst_value',
  ];
  keys.forEach((key, index) => {
    const taxKey = keys[index];
    if (product[taxKey]) {
      result += Number(product[taxKey]);
    }
  });
  return result;
};

function ReceiverDetailsDisplay({ receivers, itemId }) {
  const filteredReceivers = receivers.filter((receiver) => {
    if (receiver.item_id !== undefined && receiver.item_id !== null) {
      return String(receiver.item_id) === String(itemId);
    }
    return true;
  });

  if (filteredReceivers.length === 0) return null;

  return (
    <InfoToggle
      title={
        <div className='tw-text-[1.3rem] tw-font-thin'>
          Receivers ({filteredReceivers.length})
        </div>
      }
      showBorder={true}
      borderStyles='tw-border tw-border-solid tw-border-grey-200'
      styles='tw-p-4 tw-mt-3'
    >
      <div className='tw-self-start tw-w-full'>
        <ReceiversTable receivers={filteredReceivers} />
      </div>
    </InfoToggle>
  );
}

function SubOrderTable({ suborderBills }) {
  if (!suborderBills || !suborderBills.length) return null;
  return (
    <div className='tw-flex tw-flex-col tw-gap-4'>
      <p className='tw-text-[1.6rem] tw-font-thin'>Order Bills:</p>
      <div className='tw-flex tw-flex-col tw-gap-4'>
        {suborderBills?.map((bill) => (
          <ToggleTable key={bill?.displayName} {...bill} />
        ))}
      </div>
    </div>
  );
}

const InfoStrip = ({ title, value, isId }) => {
  return (
    <div className='tw-w-full tw-flex tw-justify-between tw-items-center'>
      <span className='tw-text-lg tw-font-thin tw-text-grey-600'>{title}</span>
      <span
        className={`${
          isId
            ? 'tw-p-1 tw-border tw-border-solid tw-border-grey-200 tw-shadow-sm tw-bg-grey-100 tw-rounded-lg tw-text-grey-700 tw-text-[1.4rem] tw-px-3'
            : 'tw-text-sm tw-text-grey-700'
        }`}
        data-dd-privacy='mask'
      >
        {value || '-'}
      </span>
    </div>
  );
};

function ToggleTable({ displayName, data }) {
  return (
    <InfoToggle
      title={<div className='tw-text-[1.5rem]'>{displayName}</div>}
      showBorder={true}
      borderStyles='tw-border tw-border-solid tw-border-grey-200'
      styles='tw-p-4 tw-h-full tw-bg-grey-100/50 tw-h-full'
    >
      <div className='tw-grid tw-grid-cols-1 tw-gap-8 tw-mt-2 tw-h-full'>
        {data?.map((suborder, index) => {
          if (!suborder || !suborder.product_list?.length) return null;
          return (
            <div key={suborder.pos_timestamp}>
              {/* Bill Index */}
              <div className='tw-p-2 tw-text-[1.5rem] tw-flex tw-items-center tw-leading-none tw-gap-3'>
                <p className='tw-whitespace-nowrap tw-text-grey-600'>
                  Bill {index + 1} / {data.length}
                </p>
                <div className='tw-h-[1.6px] tw-bg-grey-200 tw-w-full' />
              </div>

              {/* Basic billing info card */}
              <div className='tw-grid tw-grid-cols-2 tw-border tw-border-solid tw-border-grey-200 tw-rounded-lg tw-mb-4 tw-bg-white'>
                <div className='tw-flex tw-flex-col tw-gap-4 tw-p-4 tw-border-solid tw-border-grey-200 tw-border-y-0 tw-border-l-0'>
                  {[
                    {
                      title: 'POS timestamp',
                      value: getFullDate(suborder.pos_timestamp),
                    },
                    {
                      title: 'Invoice Type',
                      value: suborder.invoice_type_name,
                    },
                    {
                      title: 'Invoice Id',
                      value: suborder.invoice_id,
                      isId: true,
                    },
                  ].map((info) => (
                    <InfoStrip
                      key={info.title}
                      title={info.title}
                      value={info.value}
                      isId={info.isId}
                    />
                  ))}
                </div>
                <div className='tw-flex tw-flex-col tw-gap-4 tw-p-4'>
                  {[
                    {
                      title: 'Actual Sales',
                      value: suborder.actual_sales,
                    },
                    {
                      title: 'Total Discount',
                      value: suborder.total_discount,
                    },
                  ].map((info) => (
                    <InfoStrip
                      key={info.title}
                      title={info.title}
                      value={info.value}
                      isId={info.isId}
                    />
                  ))}
                </div>
              </div>

              {/* Items info table */}
              <div className='tw-flex tw-items-center tw-w-full tw-rounded-lg tw-border tw-border-1 tw-border-solid tw-border-grey-200  tw-overflow-x-auto tw-bg-white'>
                <div className='tw-text-lg tw-font-thin tw-w-full tw-flex tw-flex-col tw-leading-none tw-gap-4'>
                  <table className='tw-rounded-xl tw-border-none tw-table-fixed'>
                    <thead className='tw-rounded-xl'>
                      <tr className='tw-rounded-2xl'>
                        {[
                          { title: 'Product Name', width: '30%' },
                          {
                            title: 'Product Variant Description',
                            width: '20%',
                          },
                          { title: 'MRP (₹)', width: '10%' },
                          { title: 'Quantity', width: '10%' },
                          { title: 'Discount', width: '10%' },
                          { title: 'Taxes (₹)', width: '10%' },
                          { title: 'Selling price (₹)', width: '10%' },
                        ].map((col) => (
                          <th
                            title={col.title}
                            key={col.title}
                            className={`tw-w-[${col.width}] ${columnHeaderStyles}`}
                          >
                            {col.title}
                          </th>
                        ))}
                        <th
                          title='Net amount (₹)'
                          className='tw-w-[10%] tw-p-3 tw-bg-blue-200 tw-text-grey-600 tw-text-center tw-whitespace-nowrap tw-overflow-hidden tw-text-ellipsis'
                        >
                          Net amount (₹)
                        </th>
                      </tr>
                    </thead>
                    <tbody className='tw-bg-white'>
                      {suborder?.product_list.map((item, index) => {
                        if (!item) return null;
                        return (
                          <tr
                            key={item.name || index}
                            className={
                              index === suborder?.product_list.length - 1
                                ? 'tw-rounded-b-xl tw-border-none tw-shadow-none tw-text-grey-600 tw-text-center'
                                : 'tw-text-grey-600 tw-text-center tw-shadow-none tw-border tw-border-solid tw-border-grey-200 !tw-border-l-0 !tw-border-r-0'
                            }
                          >
                            <td
                              title={item.name}
                              className={`
                        tw-p-4 tw-border-r tw-border-solid tw-overflow-hidden tw-text-ellipsis tw-border-y-0 tw-border-l-0 tw-border-grey-200 tw-whitespace-nowrap hover:tw-scale-[1.01] tw-transition-all tw-ease-in-out tw-duration-200 tw-cursor-pointer tw-relative tw-group 
                        `}
                            >
                              {item.name}
                            </td>
                            {[
                              {
                                value: item.variant_description,
                              },
                              { value: item.unit_rate },
                              { value: item.quantity },
                              { value: item.unit_rate - item.selling_price },
                              { value: calculateTax(item) },
                              { value: item.selling_price },
                            ].map((field, idx) => (
                              <td
                                title={field.value ?? '-'}
                                key={idx}
                                className='tw-px-2 tw-py-1 tw-border-r tw-border-solid tw-border-y-0 tw-border-l-0 tw-border-grey-200 tw-w-[10px] tw-whitespace-nowrap tw-text-center tw-overflow-hidden tw-text-ellipsis
                                  '
                              >
                                {field.value ?? '-'}
                              </td>
                            ))}
                            <td
                              className={
                                index === suborder?.product_list.length - 1
                                  ? 'tw-rounded-br-2xl tw-border-none tw-px-2 tw-py-1'
                                  : 'tw-px-2 tw-py-1'
                              }
                            >
                              {item.selling_price * item.quantity}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </InfoToggle>
  );
}

const ProcuredQuantityWrapper = styled.div`
  background: ${({ background }) => background};
  color: ${({ color }) => color};
  font-size: 12px;
  border-radius: 6px;
  padding: 0.2rem 1rem;
  line-height: normal;
  display: inline-flex;
  align-items: center;
  font-weight: normal;
  margin-right: 5px;
`;

const OfferDetailWrapper = styled.div`
  background: ${({ background }) => background};
  color: ${({ color }) => color};
  font-size: 12px;
  border-radius: 6px;
  padding: 0.2rem 1rem;
  line-height: normal;
  display: inline-flex;
  align-items: center;
  font-weight: 500;
  margin-right: 5px;
`;

const Image = styled.img`
  height: 8rem;
  width: 8rem;
  margin-right: 5px;
`;

const TagsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  justify-content: flex-end;
`;

const RightContainer = styled.div.attrs({
  className: 'price item-price right',
})`
  display: flex;
  flex-direction: column;
  flex: 1.5;
  align-items: flex-end;
`;
